import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Anchor, Sparkles, Rocket, Coins, Star, Loader2, LogOut, Trash2, Compass, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import GrandSovereignAvatar from '../components/GrandSovereignAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function JuniorCommand() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0: idle, 1: product, 2: price, 3: pitch, 4: launch
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [pitch, setPitch] = useState('');
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem('sov_junior_token');
  const hdrs = () => ({ Authorization: `Bearer ${token()}` });

  const load = async () => {
    if (!token()) { nav('/junior'); return; }
    try {
      const { data } = await axios.get(`${API}/junior/me`, { headers: hdrs(), withCredentials: true });
      setMe(data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem('sov_junior_token'); nav('/junior'); }
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = () => {
    localStorage.removeItem('sov_junior_token');
    localStorage.removeItem('sov_junior_name');
    nav('/junior');
  };

  const reset = () => { setStep(0); setProduct(''); setPrice(''); setPitch(''); };

  const saveDraft = async () => {
    if (!product || !price) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/junior/business-forge`,
        { product, price_usd: parseFloat(price), pitch },
        { headers: hdrs(), withCredentials: true }
      );
      toast.success(`Idea saved! +${data.xp_earned} XP `);
      reset();
      await load();
    } catch { toast.error('Could not save. Try again.'); }
    setBusy(false);
  };

  const launch = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/junior/business-forge/${id}/launch`, {}, { headers: hdrs(), withCredentials: true });
      toast.success(`Ship launched! +${data.xp_earned} XP `);
      await load();
    } catch { toast.error('Launch failed.'); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await axios.delete(`${API}/junior/business-forge/${id}`, { headers: hdrs(), withCredentials: true });
      await load();
    } catch { toast.error('Could not delete.'); }
  };

  const acknowledgeSovereign = async () => {
    try {
      const { data } = await axios.post(`${API}/junior/welcome/acknowledge`, {}, { headers: hdrs(), withCredentials: true });
      if (data.xp_earned) toast.success(`+${data.xp_earned} XP · The Sovereign has acknowledged you.`);
      await load();
    } catch { /* silent — UI already closed */ }
  };

  if (loading) return <div className="px-6 py-32 text-center"><Loader2 className="text-[#D4AF37] animate-spin mx-auto" /></div>;
  if (!me) return null;

  const v = me.vault || {};
  const xpForNext = Math.ceil((me.xp + 1) / 50) * 50;
  const xpPct = Math.min(100, (me.xp / xpForNext) * 100);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 min-h-[85vh] relative">
      {/* CyberKids subtle accent gradient */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.08]" style={{
        background: 'radial-gradient(circle at 20% 10%, #00E5FF 0%, transparent 40%), radial-gradient(circle at 80% 80%, #D4AF37 0%, transparent 50%)'
      }} />

      {/* Header */}
      <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#00E5FF] mb-3 flex items-center gap-2">
            <Anchor size={12} strokeWidth={1.5} /> CyberKids Academy · Junior Command
          </div>
          <h1 className="text-4xl md:text-6xl font-extralight tracking-tight leading-[1.05]">
            Welcome, <span className="font-display italic text-[#D4AF37]">{me.full_name || me.name}</span>
          </h1>
          <div className="text-sm text-[#A3A3A3] mt-3 font-light flex items-center gap-3 flex-wrap">
            <span>{me.rank}</span>
            <span className="text-[#444]">•</span>
            <span className="text-[#D4AF37] flex items-center gap-1.5"><Star size={12} strokeWidth={1.5} /> {me.xp} XP</span>
            <span className="text-[#444]">•</span>
            <span>Age {me.age}</span>
            {me.email && (<>
              <span className="text-[#444]">•</span>
              <span className="text-[#666]">{me.email}</span>
            </>)}
          </div>
          {/* XP progress bar */}
          <div className="mt-4 max-w-sm">
            <div className="h-2 bg-[#1a1a1a] border border-[#D4AF37]/30 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#00E5FF] transition-all duration-700" style={{ width: `${xpPct}%` }} />
            </div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-2">{me.xp} / {xpForNext} XP to next rank</div>
          </div>
        </div>
        <button
          data-testid="junior-logout"
          onClick={logout}
          className="self-start md:self-end border border-[#444] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2 transition-colors flex items-center gap-2"
        >
          <LogOut size={12} strokeWidth={1.5} /> Sign Out
        </button>
      </div>

      {/* Grand Sovereign Avatar + first-login welcome */}
      <GrandSovereignAvatar
        sovereign={me.grand_sovereign}
        juniorName={me.full_name || me.name}
        isFirstLogin={!me.welcomed_at}
        onAcknowledge={acknowledgeSovereign}
      />

      {/* Treasure Chest */}
      <div data-testid="junior-treasure" className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8 relative">
        <div className="md:col-span-7 sov-border bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#D4AF37]/10 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Coins size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">My Treasure Chest</div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Long-Term Treasure (5%)</div>
              <div className="font-display text-4xl md:text-5xl text-[#D4AF37]">{fmt$(v.long_term_balance_usd)}</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#666] mt-2">Locked until age 18 · Family Legacy</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Live Adventure (2%)</div>
              <div className="font-display text-4xl md:text-5xl text-[#00E5FF]">{fmt$(v.liquid_balance_usd)}</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#666] mt-2">Spendable with Mum approval</div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#D4AF37]/20 text-xs text-[#A3A3A3] font-light leading-relaxed">
            {me.streams.explainer}
          </div>
        </div>

        <div className="md:col-span-5 sov-border bg-[#0A0A0A] p-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Recent Inflows</div>
          {me.recent_inflows.length === 0 && (
            <div className="text-sm text-[#A3A3A3] font-light py-8 text-center">
              No inflows yet — the family is sailing! Your treasure will grow with every win.
            </div>
          )}
          <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
            {me.recent_inflows.map((e) => (
              <div key={e.id} className="flex items-center justify-between border-b border-[#D4AF37]/10 pb-2">
                <div>
                  <div className="text-xs font-medium tracking-wide">{fmt$(e.amount_usd)}</div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-[#A3A3A3]">{e.stream}</div>
                </div>
                <div className="text-[9px] text-[#666]">{(e.source_label || '').slice(0, 24)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lineage strip — Ancestral roster */}
      <div data-testid="junior-lineage" className="sov-border bg-[#0A0A0A] p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Users size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">My Lineage · Sovereign Roster</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-[#D4AF37]/20 p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Mother · Ancestral Guard</div>
            <div className="font-display text-2xl text-[#D4AF37] mt-1">{me.lineage?.mother_name || 'Akanksha'}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#666] mt-2">Holds Ancestral Guard status</div>
          </div>
          <div className="border border-[#D4AF37]/20 p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Father · Ancestral Guard</div>
            <div className="font-display text-2xl text-[#D4AF37] mt-1">{me.lineage?.father_name || 'Director K'}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#666] mt-2">Holds Ancestral Guard status</div>
          </div>
          <div className="border border-[#D4AF37]/20 p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Governance</div>
            <div className="text-xs font-light text-[#F5F5F5] mt-2 leading-relaxed">{me.lineage?.governance || 'Locked until maturity.'}</div>
          </div>
        </div>
      </div>

      {/* Legacy Pulse — wealth growth tracker */}
      <div data-testid="junior-legacy-pulse" className="sov-border bg-[#0A0A0A] p-8 mb-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20" style={{
          background: 'radial-gradient(circle at 90% 50%, #D4AF37 0%, transparent 60%)'
        }} />
        <div className="relative flex items-start justify-between flex-wrap gap-6">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Legacy Pulse · Live Wealth Tracker</div>
            </div>
            <p className="text-sm text-[#A3A3A3] font-light max-w-md">
              This is how much money the family has earned. Watch your share grow with every win.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Family Total</div>
            <div className="font-display text-3xl md:text-4xl text-[#F5F5F5]">{fmt$(me.legacy_pulse?.family_cumulative_usd || 0)}</div>
          </div>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="border border-[#D4AF37]/20 p-5 bg-[#D4AF37]/5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Your Share</div>
            <div className="font-display text-3xl text-[#D4AF37] mt-2">{me.legacy_pulse?.your_share_pct || 7}%</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#666] mt-1">5% Treasure + 2% Adventure</div>
          </div>
          <div className="border border-[#00E5FF]/20 p-5 bg-[#00E5FF]/5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Your Projected Stake</div>
            <div className="font-display text-3xl text-[#00E5FF] mt-2">{fmt$(me.legacy_pulse?.your_projected_usd || 0)}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#666] mt-1">Across all family heir vaults</div>
          </div>
          <div className="border border-[#D4AF37]/20 p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Heir Vaults Sailing</div>
            <div className="font-display text-3xl text-[#F5F5F5] mt-2">{me.legacy_pulse?.vault_count || 0}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#666] mt-1">Family captains</div>
          </div>
        </div>
      </div>

      {/* Business Forge */}
      <div data-testid="junior-business-forge" className="sov-border bg-[#0A0A0A] p-8 mb-12 relative">
        <div className="flex items-center gap-3 mb-2">
          <Rocket size={20} strokeWidth={1.5} className="text-[#00E5FF]" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#00E5FF]">Business Forge</div>
        </div>
        <h2 className="text-3xl md:text-4xl font-extralight mb-2">Build your <span className="font-display italic text-[#D4AF37]">first ship</span>.</h2>
        <p className="text-sm text-[#A3A3A3] font-light mb-8 max-w-xl">
          Every Junior Admiral starts with one idea. Answer three questions, then launch your ship into the world.
        </p>

        {step === 0 && (
          <button
            data-testid="junior-start-forge"
            onClick={() => setStep(1)}
            className="px-8 py-4 bg-[#D4AF37] text-[#050505] font-medium text-sm tracking-[0.3em] uppercase hover:bg-[#E5C158] transition-colors flex items-center gap-3"
          >
            <Sparkles size={14} strokeWidth={2} /> Start a New Idea
          </button>
        )}

        {step >= 1 && (
          <div className="space-y-6 max-w-2xl">
            {/* Step indicator */}
            <div className="flex items-center gap-3">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`flex-1 h-1 ${step >= s ? 'bg-[#D4AF37]' : 'bg-[#2a2a2a]'} transition-colors`} />
              ))}
            </div>

            {/* Step 1: Product */}
            {step === 1 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">Question 1 of 3</div>
                <label className="block text-2xl md:text-3xl font-extralight mb-6">What is your <span className="text-[#D4AF37]">product</span>?</label>
                <input
                  data-testid="forge-product-input"
                  autoFocus
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="e.g. Sparkle Slime Kits, Lemonade Stand, Handmade Cards"
                  className="w-full bg-transparent border-b-2 border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-xl font-light tracking-wide"
                />
                <div className="flex gap-3 mt-6">
                  <button onClick={reset} className="px-6 py-3 border border-[#444] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:border-[#888]">Cancel</button>
                  <button
                    data-testid="forge-step1-next"
                    disabled={!product.trim()}
                    onClick={() => setStep(2)}
                    className="px-8 py-3 bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-[#E5C158] disabled:opacity-30"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Price */}
            {step === 2 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">Question 2 of 3</div>
                <label className="block text-2xl md:text-3xl font-extralight mb-6">Set your <span className="text-[#D4AF37]">price</span>.</label>
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl text-[#D4AF37] font-display">$</span>
                  <input
                    data-testid="forge-price-input"
                    autoFocus
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="15"
                    min="0.01"
                    step="0.01"
                    className="w-full bg-transparent border-b-2 border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-3xl font-display tracking-wide"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep(1)} className="px-6 py-3 border border-[#444] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:border-[#888]">← Back</button>
                  <button
                    data-testid="forge-step2-next"
                    disabled={!price || parseFloat(price) <= 0}
                    onClick={() => setStep(3)}
                    className="px-8 py-3 bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-[#E5C158] disabled:opacity-30"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Pitch + Launch */}
            {step === 3 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">Question 3 of 3</div>
                <label className="block text-2xl md:text-3xl font-extralight mb-6">Why will people <span className="text-[#D4AF37]">love it</span>?</label>
                <textarea
                  data-testid="forge-pitch-input"
                  autoFocus
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value)}
                  rows={3}
                  placeholder="In one sentence — what makes your idea special?"
                  className="w-full bg-transparent border border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none p-4 text-lg font-light tracking-wide resize-none"
                />
                <div className="mt-6 p-5 border border-[#D4AF37]/20 bg-[#D4AF37]/5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Your Ship</div>
                  <div className="text-xl font-light">{product}</div>
                  <div className="text-2xl font-display text-[#D4AF37] mt-1">${parseFloat(price || 0).toFixed(2)}</div>
                  {pitch && <div className="text-sm text-[#A3A3A3] mt-2 italic">"{pitch}"</div>}
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep(2)} className="px-6 py-3 border border-[#444] text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:border-[#888]">← Back</button>
                  <button
                    data-testid="forge-launch-ship"
                    disabled={busy}
                    onClick={saveDraft}
                    className="px-8 py-3 bg-[#D4AF37] text-[#050505] text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-[#E5C158] disabled:opacity-30 flex items-center gap-2"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} strokeWidth={2} />}
                    Launch My Ship
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fleet (her launched + drafted ships) */}
      <div data-testid="junior-fleet">
        <div className="flex items-center gap-3 mb-6">
          <Compass size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">My Fleet · {me.stats.ideas_total} ideas · {me.stats.ships_launched} launched</div>
        </div>
        {me.business_forge.length === 0 && (
          <div className="sov-border bg-[#0A0A0A] p-12 text-center text-sm text-[#A3A3A3] font-light">
            Your fleet is empty. Build your first ship above!
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {me.business_forge.map((b) => (
            <div key={b.id} className="sov-border bg-[#0A0A0A] p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-base font-light tracking-wide flex-1">{b.product}</div>
                <span className={`text-[9px] tracking-[0.25em] uppercase px-2 py-1 border ${
                  b.status === 'Launched' ? 'text-[#00E5FF] border-[#00E5FF]' : 'text-[#A3A3A3] border-[#444]'
                }`}>{b.status}</span>
              </div>
              <div className="font-display text-2xl text-[#D4AF37]">${b.price_usd.toFixed(2)}</div>
              {b.pitch && <div className="text-xs text-[#A3A3A3] italic font-light">"{b.pitch}"</div>}
              <div className="flex gap-2 mt-auto pt-3 border-t border-[#D4AF37]/10">
                {b.status !== 'Launched' && (
                  <button
                    data-testid={`forge-quick-launch-${b.id}`}
                    onClick={() => launch(b.id)}
                    disabled={busy}
                    className="flex-1 text-[10px] tracking-[0.25em] uppercase py-2 bg-[#D4AF37] text-[#050505] hover:bg-[#E5C158] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Rocket size={11} strokeWidth={2} /> Launch
                  </button>
                )}
                <button
                  data-testid={`forge-delete-${b.id}`}
                  onClick={() => remove(b.id)}
                  className="p-2 border border-[#444] text-[#666] hover:border-[#888] hover:text-[#A3A3A3]"
                  title="Delete"
                >
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
